/**
 * Loyalty and referral API.
 */
import { apiGet } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';

export interface LoyaltySummary {
  count: number;
  tier: string;
  points?: number;
}

export interface PointsHistoryItem {
  id: number | string;
  type: 'earned' | 'redeemed';
  points: number;
  description?: string;
  date?: string;
}

export interface RewardItem {
  id: number | string;
  title: string;
  description?: string;
  points: number;
  canRedeem?: boolean;
}

const loyaltyEndpoint = endpoints.loyalty;
const referralEndpoint = (endpoints as { myReferral?: string }).myReferral ?? 'referral';

export interface LoyaltyResponse {
  summary: LoyaltySummary | null;
  pointsHistory: PointsHistoryItem[];
  rewards: RewardItem[];
}

export async function fetchLoyaltyData(customer_id?: number): Promise<LoyaltyResponse> {
  try {
    const params = customer_id != null ? { customer_id } : undefined;
    const res = await apiGet<Record<string, any>>(loyaltyEndpoint, params);
    if (!(res as any).success) {
      return { summary: null, pointsHistory: [], rewards: [] };
    }
    const data = (res as any).data ?? res;
    const customer = data?.customer;
    const first = Array.isArray(customer) ? customer[0] : customer;
    const summary: LoyaltySummary =
      first && typeof first === 'object'
        ? {
            count: Number(first.count ?? first.points ?? 0),
            tier: String(first.tier ?? first.tier_name ?? 'Member'),
          }
        : { count: 0, tier: 'Member' };
    const is_event_array =
      Array.isArray(customer) &&
      customer.length > 0 &&
      typeof (customer as any[])[0] === 'object' &&
      ((customer as any[])[0].count != null || (customer as any[])[0].points != null);
    const pointsHistoryRaw = is_event_array
      ? customer
      : data?.points_history ?? data?.history ?? first?.points_history ?? first?.history ?? [];
    const pointsHistory: PointsHistoryItem[] = Array.isArray(pointsHistoryRaw)
      ? (pointsHistoryRaw as any[]).map((item: any, i: number) => {
          const type_label = String(item.type ?? '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
          const action_label = String(item.action ?? '').replace(/_/g, ' ');
          const description =
            item.description ??
            item.reason ??
            (type_label && action_label ? `${type_label} • ${action_label}` : type_label || action_label || 'Points earned');
          return {
            id: item.id ?? i,
            type: item.type === 'redeemed' ? 'redeemed' : 'earned',
            points: Number(item.count ?? item.points ?? 0),
            description,
            date: item.date ?? item.created_at,
          };
        })
      : [];
    const rewardsRaw = data?.rewards ?? first?.rewards ?? [];
    const rewards: RewardItem[] = Array.isArray(rewardsRaw)
      ? rewardsRaw.map((r: any, i: number) => ({
          id: r.id ?? i,
          title: r.title ?? r.name ?? '',
          description: r.description,
          points: Number(r.points ?? 0),
          canRedeem: r.canRedeem ?? r.can_redeem,
        }))
      : [];
    return { summary, pointsHistory, rewards };
  } catch {
    return { summary: null, pointsHistory: [], rewards: [] };
  }
}

export async function fetchLoyalty(customer_id?: number): Promise<LoyaltySummary | null> {
  const { summary } = await fetchLoyaltyData(customer_id);
  return summary;
}

export interface ReferralResponse {
  referrals: any[];
  referralHistory: any[];
}

export async function fetchReferralData(customer_id: number): Promise<ReferralResponse> {
  try {
    const res = await apiGet<Record<string, any>>(referralEndpoint, { customer_id });
    if (!(res as any).success) {
      return { referrals: [], referralHistory: [] };
    }
    const data = (res as any).data ?? res;
    const customer = data?.customer;
    const list = Array.isArray(customer) ? customer : (customer?.referrals ?? data?.referrals ?? (customer && typeof customer === 'object' ? [customer] : []));
    const referrals = Array.isArray(list) ? list : [];
    const referralHistory = data?.referral_history ?? data?.history ?? (Array.isArray(customer) ? customer : referrals);
    return {
      referrals,
      referralHistory: Array.isArray(referralHistory) ? referralHistory : referrals,
    };
  } catch {
    return { referrals: [], referralHistory: [] };
  }
}

export async function fetchReferrals(customer_id: number): Promise<any[]> {
  const { referrals } = await fetchReferralData(customer_id);
  return referrals;
}
